import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import Layout from '../components/Layout';
import AccountDetailModal from '../components/AccountDetailModal';
import { useAuth } from '../context/AuthContext';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faPhone, faEnvelope, faMapMarkerAlt,
    faCheckCircle, faPlus, faSearch, faTimes, faSyncAlt,
    faTable, faColumns, faBullseye,
    faClock, faComments,
    faBan, faUndo, faChevronRight,
    faCheck, faBriefcase, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';

const LinkedInIcon = () => (
    <svg className="lead-linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.45a1.64 1.64 0 1 0 0 3.28 1.64 1.64 0 0 0 0-3.28z" />
    </svg>
);

const formatLinkedInUrl = (url) => {
    if (!url || !url.trim()) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith('www.') || trimmed.startsWith('linkedin.com')) return `https://${trimmed}`;
    return `https://www.linkedin.com/in/${trimmed}`;
};

const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
        const d = new Date(isoString);
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return String(isoString);
    }
};

const OPPORTUNITY_STAGES = [
    "Qualification",
    "Approached",
    "Follow-up",
    "Meeting Scheduled",
    "Proposal Preparation",
    "Review/Discussion",
    "Completed"
];

const CONTACT_METHODS = [
    { value: 'Phone Call', label: '📞 Phone Call' },
    { value: 'Email', label: '✉️ Email Outreach' },
    { value: 'LinkedIn Message', label: '💼 LinkedIn InMail / Message' },
    { value: 'In-Person Meeting', label: '🤝 In-Person Meeting' },
    { value: 'WhatsApp / SMS', label: '💬 WhatsApp / SMS' },
    { value: 'Other', label: '📌 Other Channel' }
];

const REJECTION_REASONS = [
    'Not Interested / Budget Constraints',
    'Competitor Solution Chosen',
    'Invalid Lead / Wrong Contact Info',
    'No Response / Cold Lead',
    'Project Postponed / Cancelled',
    'Other / Unspecified'
];

export default function Outreach() {
    const navigate = useNavigate();
    const { empId, roleId, teamMemberIds, isSuper, isManager, isEmployee, getRbacParams, activeOrgId, canWrite } = useAuth();

    const [leads, setLeads] = useState([]);
    const [users, setUsers] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [targets, setTargets] = useState([]);
    const [loading, setLoading] = useState(true);

    // View mode: 'table' or 'kanban'
    const [viewMode, setViewMode] = useState('table');

    // Filters
    const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, 'New Lead', 'Contacted', 'Opportunity Created', 'Rejected'
    const [priorityFilter, setPriorityFilter] = useState('ALL'); // ALL, High, Medium, Low
    const [repFilter, setRepFilter] = useState('ALL');
    const [search, setSearch] = useState('');

    // Pagination for Table View
    const [currentPage, setCurrentPage] = useState(1);
    const [entries, setEntries] = useState(15);

    // Selection for Batch Actions
    const [selectedIds, setSelectedIds] = useState([]);

    // Modals
    const [detailAccount, setDetailAccount] = useState(null);
    const [contactModal, setContactModal] = useState({
        open: false,
        lead: null,
        method: 'Phone Call',
        notes: ''
    });
    const [oppModal, setOppModal] = useState({
        open: false,
        lead: null,
        oppName: '',
        amount: 5000,
        stage: 'Qualification',
        closeDate: '',
        notes: ''
    });
    const [rejectModal, setRejectModal] = useState({
        open: false,
        lead: null,
        reason: 'Not Interested / Budget Constraints',
        notes: ''
    });

    const [actionToast, setActionToast] = useState('');

    const showToast = (msg) => {
        setActionToast(msg);
        setTimeout(() => setActionToast(''), 3500);
    };

    // RBAC: Only the designated responsible person assigned to the lead can perform outreach actions
    const canManageLead = (lead) => {
        if (!lead) return false;
        const leadRep = Number(lead.responsible || lead.assignedTo);
        return leadRep === Number(empId);
    };

    // Filter assignable users according to RBAC
    const assignableUsers = useMemo(() => {
        return users.filter(u => {
            if (roleId && Number(u.RoleID) > Number(roleId)) return false;
            if (isSuper) return true;
            if (isManager) {
                return (
                    Number(u.ReportingTo) === Number(empId) ||
                    Number(u.empId) === Number(empId) ||
                    (teamMemberIds && teamMemberIds.map(Number).includes(Number(u.empId)))
                );
            }
            return Number(u.empId) === Number(empId);
        });
    }, [users, roleId, empId, isSuper, isManager, teamMemberIds]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();

            const [leadsRes, usersRes, oppsRes, targetsRes] = await Promise.all([
                api.get(`/leads?${qstr}`),
                api.get(`/users?${qstr}`),
                api.get(`/opportunities?${qstr}`),
                api.get(`/targets?${qstr}`)
            ]);

            const mappedLeads = (leadsRes.data || []).map(item => ({
                id: item.AccountID,
                name: item.AccountName || '',
                type: item.AccountType || 'Organization',
                contactPerson: item.ContactPerson || '',
                designation: item.ContactDesignation || '',
                phone: item.CntPhone1 || '',
                email: item.CntMail1 || '',
                phone2: item.CntPhone2 || '',
                email2: item.CntMail2 || '',
                city: item.City || '',
                linkedin: item.Linkedin || '',
                status: item.Status || 'Lead',
                priority: item.Priority || 'Medium',
                approachStatus: item.ApproachStatus || 'New Lead',
                contactedAt: item.ContactedAt,
                contactMethod: item.ContactMethod,
                contactNotes: item.ContactNotes,
                assignedTo: item.AssignedTo,
                assignedToName: item.AssignedToName,
                leadGenerator: item.LeadGenerator,
                leadGeneratorName: item.LeadGeneratorName,
                responsible: item.Responsible,
                responsibleName: item.ResponsibleName,
                notes: item.Notes || '',
                createdAt: item.CreatedAt,
                updatedAt: item.UpdatedAt
            }));

            setLeads(mappedLeads);
            setUsers(usersRes.data || []);
            setOpportunities(oppsRes.data || []);
            setTargets(targetsRes.data || []);
        } catch (err) {
            console.error('Failed to load outreach data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [empId, roleId, activeOrgId]);

    // Map opportunities to accounts for instant status check (by ID and by Name)
    const oppsByAccount = useMemo(() => {
        const map = {};
        opportunities.forEach(opp => {
            if (opp.Account) {
                const idKey = String(opp.Account);
                if (!map[idKey]) map[idKey] = [];
                map[idKey].push(opp);
            }
            if (opp.AccountName) {
                const nameKey = String(opp.AccountName).toLowerCase().trim();
                if (!map[nameKey]) map[nameKey] = [];
                map[nameKey].push(opp);
            }
        });
        return map;
    }, [opportunities]);

    const getLeadOpps = (lead) => {
        if (!lead) return [];
        const byId = oppsByAccount[String(lead.id)] || [];
        const byName = lead.name ? (oppsByAccount[lead.name.toLowerCase().trim()] || []) : [];
        const seen = new Set();
        return [...byId, ...byName].filter(o => {
            if (seen.has(o.OprID)) return false;
            seen.add(o.OprID);
            return true;
        });
    };

    const getLeadEffectiveStatus = (lead) => {
        if (!lead) return 'New Lead';
        if (lead.approachStatus === 'Opportunity Created') return 'Opportunity Created';
        const opps = getLeadOpps(lead);
        if (opps.length > 0) return 'Opportunity Created';
        return lead.approachStatus || 'New Lead';
    };

    // Filter leads based on search, status, priority, rep
    const filteredLeads = useMemo(() => {
        return leads.filter(l => {
            const effStatus = getLeadEffectiveStatus(l);

            // Status filter: 4 statuses
            if (statusFilter !== 'ALL' && effStatus !== statusFilter) {
                return false;
            }

            // Priority filter
            if (priorityFilter !== 'ALL' && (l.priority || '').toLowerCase() !== priorityFilter.toLowerCase()) {
                return false;
            }

            // Rep filter
            if (repFilter !== 'ALL') {
                const rep = String(l.responsible || l.assignedTo || '');
                if (rep !== String(repFilter)) return false;
            }

            // Search query
            if (search.trim()) {
                const q = search.toLowerCase();
                const matchName = l.name.toLowerCase().includes(q);
                const matchPerson = l.contactPerson.toLowerCase().includes(q);
                const matchCity = l.city.toLowerCase().includes(q);
                const matchPhone = l.phone.toLowerCase().includes(q);
                const matchEmail = l.email.toLowerCase().includes(q);
                const matchNotes = (l.notes || '').toLowerCase().includes(q);
                const matchContactNotes = (l.contactNotes || '').toLowerCase().includes(q);
                const matchId = String(l.id).includes(q);

                if (!matchName && !matchPerson && !matchCity && !matchPhone && !matchEmail && !matchNotes && !matchContactNotes && !matchId) {
                    return false;
                }
            }

            return true;
        });
    }, [leads, statusFilter, priorityFilter, repFilter, search, oppsByAccount]);

    // Target Progress Calculation
    const targetProgress = useMemo(() => {
        const approachTargets = targets.filter(t => t.TargetType === 'Lead Approaching');
        const quota = approachTargets.reduce((sum, t) => sum + (Number(t.TargetCount) || 0), 0);
        // Approached = Contacted + Opportunity Created
        const achieved = leads.filter(l => {
            const effStatus = getLeadEffectiveStatus(l);
            return effStatus === 'Contacted' || effStatus === 'Opportunity Created';
        }).length;
        const percent = quota > 0 ? Math.min(100, Math.round((achieved / quota) * 100)) : (achieved > 0 ? 100 : 0);
        return { quota, achieved, percent };
    }, [targets, leads, oppsByAccount]);

    // Metrics for KPI Ribbon (Unified 5-card ribbon)
    const metrics = useMemo(() => {
        const total = leads.length;
        const newLeads = leads.filter(l => getLeadEffectiveStatus(l) === 'New Lead').length;
        const contacted = leads.filter(l => getLeadEffectiveStatus(l) === 'Contacted').length;
        const converted = leads.filter(l => getLeadEffectiveStatus(l) === 'Opportunity Created').length;
        const rejected = leads.filter(l => getLeadEffectiveStatus(l) === 'Rejected').length;
        return { total, newLeads, contacted, converted, rejected };
    }, [leads, oppsByAccount]);

    // Pagination for Table View
    const totalPages = Math.ceil(filteredLeads.length / entries);
    const paginatedLeads = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return filteredLeads.slice(start, start + entries);
    }, [filteredLeads, currentPage, entries]);

    // ==========================================
    // ACTIONS: Outreach Status Changes
    // ==========================================

    // 1. Mark as Contacted / Log Outreach
    const handleOpenContactModal = (lead, e) => {
        if (e) e.stopPropagation();
        if (!canManageLead(lead)) {
            alert("You only have permission to update outreach for leads assigned to you.");
            return;
        }
        setContactModal({
            open: true,
            lead,
            method: lead.contactMethod || 'Phone Call',
            notes: lead.contactNotes || ''
        });
    };

    const handleSaveContactLog = async (e) => {
        e.preventDefault();
        const { lead, method, notes } = contactModal;
        if (!lead) return;

        try {
            await api.patch(`/leads/${lead.id}/approach-status`, {
                ApproachStatus: 'Contacted',
                ContactMethod: method,
                ContactNotes: notes
            });

            setLeads(prev => prev.map(l => {
                if (l.id === lead.id) {
                    return {
                        ...l,
                        approachStatus: 'Contacted',
                        contactedAt: new Date().toISOString(),
                        contactMethod: method,
                        contactNotes: notes
                    };
                }
                return l;
            }));

            setContactModal({ open: false, lead: null, method: 'Phone Call', notes: '' });
            showToast(`Lead #${lead.id} (${lead.name}) marked as Contacted!`);
        } catch (err) {
            console.error('Failed to update outreach status:', err);
            alert('Failed to log contact. Please try again.');
        }
    };

    // Quick 1-click Contact Toggle
    const handleQuickMarkContacted = async (lead, e) => {
        if (e) e.stopPropagation();
        if (!canManageLead(lead)) {
            alert("You only have permission to update outreach for leads assigned to you.");
            return;
        }

        try {
            await api.patch(`/leads/${lead.id}/approach-status`, {
                ApproachStatus: 'Contacted',
                ContactMethod: lead.contactMethod || 'Phone Call'
            });

            setLeads(prev => prev.map(l => {
                if (l.id === lead.id) {
                    return {
                        ...l,
                        approachStatus: 'Contacted',
                        contactedAt: new Date().toISOString(),
                        contactMethod: l.contactMethod || 'Phone Call'
                    };
                }
                return l;
            }));

            showToast(`Lead #${lead.id} marked as Contacted!`);
        } catch (err) {
            console.error('Failed to quick-mark as contacted:', err);
            alert('Failed to update status.');
        }
    };

    // 2. Create Opportunity from Contacted Lead
    const handleOpenCreateOppModal = (lead, e) => {
        if (e) e.stopPropagation();
        if (!canManageLead(lead)) {
            alert("You only have permission to create opportunities for leads assigned to you.");
            return;
        }

        const now = new Date();
        const defaultClose = new Date(now.setDate(now.getDate() + 30)).toISOString().split('T')[0];

        setOppModal({
            open: true,
            lead,
            oppName: `${lead.name} - Deal`,
            amount: 5000,
            stage: 'Qualification',
            closeDate: defaultClose,
            notes: lead.contactNotes ? `Converted from outreach. Notes: ${lead.contactNotes}` : ''
        });
    };

    const handleSaveOpportunity = async (e) => {
        e.preventDefault();
        const { lead, oppName, amount, stage, closeDate, notes } = oppModal;
        if (!lead || !oppName.trim()) {
            alert("Opportunity Name is required.");
            return;
        }

        try {
            const oppPayload = {
                OpprName: oppName.trim(),
                Account: Number(lead.id) || null,
                Amount: Number(amount) || 0,
                Stage: stage || 'Qualification',
                Status: 'Open',
                Probability: 20,
                Owner: Number(lead.responsible || lead.assignedTo || empId) || null,
                CloseDate: closeDate || null,
                Notes: notes || `Created from Outreach on ${new Date().toLocaleDateString()}`
            };

            const oppRes = await api.post('/opportunities', oppPayload);

            // Update local lead approach status
            setLeads(prev => prev.map(l => {
                if (l.id === lead.id) {
                    return {
                        ...l,
                        approachStatus: 'Opportunity Created'
                    };
                }
                return l;
            }));

            // Add newly created opp to list
            if (oppRes.data && oppRes.data.id) {
                setOpportunities(prev => [...prev, {
                    OpprID: oppRes.data.id,
                    ...oppPayload
                }]);
            }

            setOppModal({ open: false, lead: null, oppName: '', amount: 5000, stage: 'Qualification', closeDate: '', notes: '' });
            showToast(`Opportunity created for ${lead.name}! Lead marked as Opportunity Created.`);
        } catch (err) {
            console.error('Failed to create opportunity from lead:', err);
            alert('Failed to create opportunity. Please check details.');
        }
    };

    // 3. Mark as Rejected / Dead
    const handleOpenRejectModal = (lead, e) => {
        if (e) e.stopPropagation();
        if (!canManageLead(lead)) {
            alert("You only have permission to update outreach for leads assigned to you.");
            return;
        }

        setRejectModal({
            open: true,
            lead,
            reason: 'Not Interested / Budget Constraints',
            notes: ''
        });
    };

    const handleSaveRejection = async (e) => {
        e.preventDefault();
        const { lead, reason, notes } = rejectModal;
        if (!lead) return;

        const fullNotes = `Rejected / Disqualified: ${reason}. ${notes ? `Notes: ${notes}` : ''}`.trim();

        try {
            await api.patch(`/leads/${lead.id}/approach-status`, {
                ApproachStatus: 'Rejected',
                ContactNotes: fullNotes
            });

            setLeads(prev => prev.map(l => {
                if (l.id === lead.id) {
                    return {
                        ...l,
                        approachStatus: 'Rejected',
                        contactNotes: fullNotes
                    };
                }
                return l;
            }));

            setRejectModal({ open: false, lead: null, reason: 'Not Interested / Budget Constraints', notes: '' });
            showToast(`Lead #${lead.id} (${lead.name}) marked as Rejected.`);
        } catch (err) {
            console.error('Failed to mark lead as rejected:', err);
            alert('Failed to update status.');
        }
    };

    // 4. Re-Open Rejected Lead (Reset to New Lead or Contacted)
    const handleReopenLead = async (lead, targetStatus = 'New Lead', e) => {
        if (e) e.stopPropagation();
        if (!canManageLead(lead)) {
            alert("You only have permission to update outreach for leads assigned to you.");
            return;
        }

        try {
            await api.patch(`/leads/${lead.id}/approach-status`, {
                ApproachStatus: targetStatus,
                ContactNotes: `Re-opened on ${new Date().toLocaleDateString()}. Previous note: ${lead.contactNotes || ''}`
            });

            setLeads(prev => prev.map(l => {
                if (l.id === lead.id) {
                    return {
                        ...l,
                        approachStatus: targetStatus
                    };
                }
                return l;
            }));

            showToast(`Lead #${lead.id} re-opened as ${targetStatus}!`);
        } catch (err) {
            console.error('Failed to re-open lead:', err);
            alert('Failed to update status.');
        }
    };

    // Batch Actions
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Select all leads user can manage on the current page
            const selectable = paginatedLeads.filter(canManageLead).map(l => l.id);
            setSelectedIds(selectable);
        } else {
            setSelectedIds([]);
        }
    };

    const handleToggleSelect = (id, e) => {
        e.stopPropagation();
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleBatchMarkContacted = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Mark ${selectedIds.length} selected leads as Contacted?`)) return;

        try {
            await api.post('/leads/batch-approach-status', {
                ids: selectedIds,
                ApproachStatus: 'Contacted',
                ContactMethod: 'Bulk Outreach'
            });

            setLeads(prev => prev.map(l => {
                if (selectedIds.includes(l.id)) {
                    return {
                        ...l,
                        approachStatus: 'Contacted',
                        contactedAt: new Date().toISOString(),
                        contactMethod: 'Bulk Outreach'
                    };
                }
                return l;
            }));

            showToast(`Successfully marked ${selectedIds.length} leads as Contacted!`);
            setSelectedIds([]);
        } catch (err) {
            console.error('Batch update failed:', err);
            alert('Failed to batch update leads.');
        }
    };

    const handleBatchMarkRejected = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Mark ${selectedIds.length} selected leads as Rejected / Dead?`)) return;

        try {
            await api.post('/leads/batch-approach-status', {
                ids: selectedIds,
                ApproachStatus: 'Rejected',
                ContactNotes: `Batch marked as Rejected on ${new Date().toLocaleDateString()}`
            });

            setLeads(prev => prev.map(l => {
                if (selectedIds.includes(l.id)) {
                    return {
                        ...l,
                        approachStatus: 'Rejected',
                        contactNotes: `Batch marked as Rejected on ${new Date().toLocaleDateString()}`
                    };
                }
                return l;
            }));

            showToast(`Successfully marked ${selectedIds.length} leads as Rejected!`);
            setSelectedIds([]);
        } catch (err) {
            console.error('Batch rejection failed:', err);
            alert('Failed to batch update leads.');
        }
    };

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* TOAST NOTIFICATION */}
                {actionToast && (
                    <div className="approaching-toast">
                        <FontAwesomeIcon icon={faCheckCircle} className="toast-icon" />
                        <span>{actionToast}</span>
                    </div>
                )}

                {/* ================= 1. TOP HEADER ================= */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Outreach Management</h1>
                    </div>

                    <div className="dome-dash-actions">
                        {/* View Switcher: Table vs Kanban */}
                        <div className="dome-period-pills">
                            <button
                                type="button"
                                className={`dome-period-btn ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                                title="Switch to Table View"
                            >
                                <FontAwesomeIcon icon={faTable} className="icon-mr-4" />
                                Table
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${viewMode === 'kanban' ? 'active' : ''}`}
                                onClick={() => setViewMode('kanban')}
                                title="Switch to Pipeline Board"
                            >
                                <FontAwesomeIcon icon={faColumns} className="icon-mr-4" />
                                Pipeline Board
                            </button>
                        </div>

                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={fetchData}
                            disabled={loading}
                            title="Refresh Outreach Data"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* ================= 2. 6-CARD KPI RIBBON ================= */}
                <div className="dome-kpi-strip dome-outreach-kpi-strip">
                    {/* 1. Total Assigned */}
                    <div
                        className={`dome-kpi-card ${statusFilter === 'ALL' ? 'active-filter-card' : ''}`}
                        onClick={() => { setStatusFilter('ALL'); setCurrentPage(1); }}
                        title="Show All Leads"
                    >
                        <span className="dome-kpi-label">Total Leads</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{metrics.total}</span>
                        </div>
                        <span className="dome-kpi-sub">Assigned accounts</span>
                    </div>

                    {/* 2. New Leads */}
                    <div
                        className={`dome-kpi-card ${statusFilter === 'New Lead' ? 'active-filter-card' : ''}`}
                        onClick={() => { setStatusFilter('New Lead'); setCurrentPage(1); }}
                        title="Filter: New Leads"
                    >
                        <span className="dome-kpi-label">New Leads</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-primary">{metrics.newLeads}</span>
                        </div>
                        <span className="dome-kpi-sub">Pending initial contact</span>
                    </div>

                    {/* 3. Contacted */}
                    <div
                        className={`dome-kpi-card ${statusFilter === 'Contacted' ? 'active-filter-card' : ''}`}
                        onClick={() => { setStatusFilter('Contacted'); setCurrentPage(1); }}
                        title="Filter: Contacted Leads"
                    >
                        <span className="dome-kpi-label">Contacted</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{metrics.contacted}</span>
                        </div>
                        <span className="dome-kpi-sub">Approached &amp; engaged</span>
                    </div>

                    {/* 4. Converted (Opportunity Created) */}
                    <div
                        className={`dome-kpi-card ${statusFilter === 'Opportunity Created' ? 'active-filter-card' : ''}`}
                        onClick={() => { setStatusFilter('Opportunity Created'); setCurrentPage(1); }}
                        title="Filter: Converted Deals"
                    >
                        <span className="dome-kpi-label">Opportunities</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{metrics.converted}</span>
                        </div>
                        <span className="dome-kpi-sub">Converted to deals</span>
                    </div>

                    {/* 5. Rejected / Dead */}
                    <div
                        className={`dome-kpi-card ${statusFilter === 'Rejected' ? 'active-filter-card' : ''}`}
                        onClick={() => { setStatusFilter('Rejected'); setCurrentPage(1); }}
                        title="Filter: Disqualified Leads"
                    >
                        <span className="dome-kpi-label">Rejected / Dead</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{metrics.rejected}</span>
                        </div>
                        <span className="dome-kpi-sub">Disqualified leads</span>
                    </div>

                    {/* 6. Target Progress Quota */}
                    <div
                        className="dome-kpi-card"
                        onClick={() => navigate('/targets')}
                        title="View Outreach Quotas in Targets"
                    >
                        <span className="dome-kpi-label">Outreach Target</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{targetProgress.achieved} / {targetProgress.quota}</span>
                        </div>
                        <span className="dome-kpi-sub">{targetProgress.percent}% achieved</span>
                    </div>
                </div>

                {/* ================= 3. COMPACT FILTER TOOLBAR STRIP ================= */}
                <div className="dome-dash-subnav-strip dome-leads-subnav">
                    <div className="dome-leads-toolbar-left">
                        <div className="dome-period-pills">
                            <button
                                type="button"
                                className={`dome-period-btn ${statusFilter === 'ALL' ? 'active' : ''}`}
                                onClick={() => { setStatusFilter('ALL'); setCurrentPage(1); }}
                            >
                                All ({leads.length})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${statusFilter === 'New Lead' ? 'active' : ''}`}
                                onClick={() => { setStatusFilter('New Lead'); setCurrentPage(1); }}
                            >
                                New ({metrics.newLeads})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${statusFilter === 'Contacted' ? 'active' : ''}`}
                                onClick={() => { setStatusFilter('Contacted'); setCurrentPage(1); }}
                            >
                                Contacted ({metrics.contacted})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${statusFilter === 'Opportunity Created' ? 'active' : ''}`}
                                onClick={() => { setStatusFilter('Opportunity Created'); setCurrentPage(1); }}
                            >
                                Opp Created ({metrics.converted})
                            </button>
                            <button
                                type="button"
                                className={`dome-period-btn ${statusFilter === 'Rejected' ? 'active' : ''}`}
                                onClick={() => { setStatusFilter('Rejected'); setCurrentPage(1); }}
                            >
                                Rejected ({metrics.rejected})
                            </button>
                        </div>

                        <div className="dome-search-mini-box">
                            <FontAwesomeIcon icon={faSearch} className="dome-search-mini-icon" />
                            <input
                                type="text"
                                placeholder="Search company, contact, phone, email, notes..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                className="dome-search-mini-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dome-search-clear-mini"
                                    onClick={() => { setSearch(''); setCurrentPage(1); }}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="dome-leads-toolbar-right">
                        <div className="dome-mini-filter-item">
                            <span className="dome-filter-mini-label">Priority:</span>
                            <select
                                value={priorityFilter}
                                onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-mini-select"
                            >
                                <option value="ALL">All</option>
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                        </div>

                        {!isEmployee && (
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Rep:</span>
                                <select
                                    value={repFilter}
                                    onChange={(e) => { setRepFilter(e.target.value); setCurrentPage(1); }}
                                    className="dome-mini-select"
                                >
                                    <option value="ALL">All Members</option>
                                    {assignableUsers.map(u => (
                                        <option key={u.empId} value={u.empId}>
                                            {u.name} ({u.empId})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {(statusFilter !== 'ALL' || priorityFilter !== 'ALL' || repFilter !== 'ALL' || search) && (
                            <button
                                type="button"
                                className="dome-clear-filters-mini"
                                onClick={() => {
                                    setStatusFilter('ALL');
                                    setPriorityFilter('ALL');
                                    setRepFilter('ALL');
                                    setSearch('');
                                    setCurrentPage(1);
                                }}
                                title="Reset all active filters"
                            >
                                <FontAwesomeIcon icon={faTimesCircle} />
                                <span>Reset</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* ================= BATCH ACTION BAR ================= */}
                {selectedIds.length > 0 && (
                    <div className="dome-batch-action-bar">
                        <div className="dome-batch-info">
                            <FontAwesomeIcon icon={faCheckCircle} />
                            <span><strong>{selectedIds.length}</strong> lead(s) selected</span>
                        </div>
                        <div className="dome-batch-buttons">
                            <button
                                type="button"
                                className="btn-dome-new"
                                onClick={handleBatchMarkContacted}
                            >
                                <FontAwesomeIcon icon={faCheck} className="icon-mr-4" />
                                Mark as Contacted
                            </button>
                            <button
                                type="button"
                                className="btn-dome-secondary dome-btn-danger"
                                onClick={handleBatchMarkRejected}
                            >
                                <FontAwesomeIcon icon={faBan} className="icon-mr-4" />
                                Mark as Rejected
                            </button>
                            <button
                                type="button"
                                className="btn-dome-secondary"
                                onClick={() => setSelectedIds([])}
                            >
                                Clear Selection
                            </button>
                        </div>
                    </div>
                )}

                {/* ================= 4. DOME CARD CONTAINER ================= */}
                <div className="dome-dash-card">

                    {/* CARD HEADER */}
                    <div className="dome-dash-card-header">
                        <h3 className="dome-dash-card-title">
                            {viewMode === 'table' ? `Outreach Pipeline (${filteredLeads.length})` : `Pipeline Board (${filteredLeads.length})`}
                        </h3>

                        {viewMode === 'table' && (
                            <div className="dome-table-header-controls">
                                <div className="dome-mini-filter-item">
                                    <span className="dome-filter-mini-label">Show:</span>
                                    <select
                                        value={entries}
                                        onChange={(e) => {
                                            setEntries(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="dome-mini-select"
                                    >
                                        <option value={10}>10</option>
                                        <option value={15}>15</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ================= VIEW MODE 1: TABLE VIEW ================= */}
                    {viewMode === 'table' && (
                        <>
                            <div className="dome-dash-table-wrap">
                                <table className="dome-dash-table dome-outreach-table">
                                    <thead>
                                        <tr>
                                            <th className="th-checkbox">
                                                <input
                                                    type="checkbox"
                                                    onChange={handleSelectAll}
                                                    checked={
                                                        paginatedLeads.length > 0 &&
                                                        paginatedLeads.filter(canManageLead).length > 0 &&
                                                        paginatedLeads.filter(canManageLead).every(l => selectedIds.includes(l.id))
                                                    }
                                                />
                                            </th>
                                            <th className="th-id">ID</th>
                                            <th>Account Name</th>
                                            <th>Contact Person</th>
                                            <th>Direct Channels</th>
                                            <th>Responsible Rep</th>
                                            <th>Priority</th>
                                            <th>Outreach Status</th>
                                            <th>Contact Timestamp</th>
                                            <th className="th-action">Outreach Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="10" className="td-empty text-center">
                                                    Loading outreach records...
                                                </td>
                                            </tr>
                                        ) : paginatedLeads.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" className="td-empty text-center">
                                                    No outreach leads match your filters
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedLeads.map((lead) => {
                                                const isUserAllowed = canManageLead(lead);
                                                const effStatus = getLeadEffectiveStatus(lead);
                                                const linkedOpps = getLeadOpps(lead);

                                                return (
                                                    <tr
                                                        key={lead.id}
                                                        className={`clickable-row ${selectedIds.includes(lead.id) ? 'row-selected' : ''}`}
                                                        onClick={() => setDetailAccount(lead)}
                                                        title="Click to view full lead details"
                                                    >
                                                        {/* Select Checkbox */}
                                                        <td className="td-center" onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.includes(lead.id)}
                                                                onChange={(e) => handleToggleSelect(lead.id, e)}
                                                                disabled={!isUserAllowed}
                                                            />
                                                        </td>

                                                        {/* ID */}
                                                        <td>
                                                            <span className="dome-id-pill">{lead.id}</span>
                                                        </td>

                                                        {/* Account Name */}
                                                        <td>
                                                            <div className="dome-lead-name-col">
                                                                <div className="flex-align-center gap-6">
                                                                    <span className="dome-lead-name-link">
                                                                        {lead.name}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Contact Person */}
                                                        <td>
                                                            <div className="dome-lead-name-col">
                                                                <span className="font-w-600">
                                                                    {lead.contactPerson || '—'}
                                                                </span>
                                                                {lead.designation && (
                                                                    <span className="contact-designation-text">
                                                                        {lead.designation}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* Direct Outreach Channels */}
                                                        <td onClick={(e) => e.stopPropagation()}>
                                                            <div className="direct-outreach-icons">
                                                                {lead.phone ? (
                                                                    <a
                                                                        href={`tel:${lead.phone}`}
                                                                        className="outreach-btn-link phone"
                                                                        title={`Call ${lead.phone}`}
                                                                    >
                                                                        <FontAwesomeIcon icon={faPhone} />
                                                                    </a>
                                                                ) : (
                                                                    <span className="outreach-disabled"><FontAwesomeIcon icon={faPhone} /></span>
                                                                )}

                                                                {lead.email ? (
                                                                    <a
                                                                        href={`mailto:${lead.email}`}
                                                                        className="outreach-btn-link mail"
                                                                        title={`Email ${lead.email}`}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEnvelope} />
                                                                    </a>
                                                                ) : (
                                                                    <span className="outreach-disabled"><FontAwesomeIcon icon={faEnvelope} /></span>
                                                                )}

                                                                {lead.linkedin ? (
                                                                    <a
                                                                        href={formatLinkedInUrl(lead.linkedin)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="outreach-btn-link linkedin"
                                                                        title="Open LinkedIn Profile"
                                                                    >
                                                                        <LinkedInIcon />
                                                                    </a>
                                                                ) : null}
                                                            </div>
                                                        </td>

                                                        {/* Responsible Rep */}
                                                        <td>
                                                            <div className="dome-resp-badge">

                                                                <span className="font-w-500 font-fs-12">
                                                                    {lead.responsibleName || lead.assignedToName || '—'}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Priority */}
                                                        <td>
                                                            <span className={`dome-badge-select dome-priority-${(lead.priority || 'medium').toLowerCase()}`}>
                                                                {lead.priority || 'Medium'}
                                                            </span>
                                                        </td>

                                                        {/* Approach Status: 4 Badges */}
                                                        <td>
                                                            <div className="approach-badge-wrap">
                                                                {effStatus === 'Contacted' ? (
                                                                    <span className="badge-status badge-submitted">
                                                                        <span className="dome-dot dot-submitted"></span> Contacted
                                                                    </span>
                                                                ) : effStatus === 'Opportunity Created' ? (
                                                                    <span className="badge-status badge-paid">
                                                                        <span className="dome-dot dot-paid"></span> Opp Created
                                                                    </span>
                                                                ) : effStatus === 'Rejected' ? (
                                                                    <span className="badge-status badge-rejected">
                                                                        <span className="dome-dot dot-rejected"></span> Rejected
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge-status badge-neutral">
                                                                        <span className="dome-dot dot-neutral"></span> New Lead
                                                                    </span>
                                                                )}

                                                                {lead.contactMethod && (
                                                                    <small className="contact-method-sub">
                                                                        via {lead.contactMethod}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* Last Contacted Timestamp */}
                                                        <td>
                                                            <span className={lead.contactedAt ? 'contacted-date-active' : 'text-muted'}>
                                                                {formatDate(lead.contactedAt)}
                                                            </span>
                                                        </td>

                                                        {/* Contextual Outreach Actions */}
                                                        <td onClick={(e) => e.stopPropagation()}>
                                                            <div className="dome-row-actions">

                                                                {/* Status 1: New Lead */}
                                                                {effStatus === 'New Lead' && (
                                                                    isUserAllowed ? (
                                                                        <>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-dome-new btn-xs"
                                                                                title="Log Outreach and mark as Contacted"
                                                                                onClick={(e) => handleOpenContactModal(lead, e)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faCheck} className="icon-mr-4" />
                                                                                Log
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="dome-icon-btn dome-icon-btn-danger"
                                                                                title="Mark as Rejected / Dead"
                                                                                onClick={(e) => handleOpenRejectModal(lead, e)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faBan} />
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-secondary text-11">
                                                                            View Only
                                                                        </span>
                                                                    )
                                                                )}

                                                                {/* Status 2: Contacted */}
                                                                {effStatus === 'Contacted' && (
                                                                    isUserAllowed ? (
                                                                        <>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-dome-new btn-xs"
                                                                                title="Convert this contacted lead into an Opportunity Deal"
                                                                                onClick={(e) => handleOpenCreateOppModal(lead, e)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faPlus} className="icon-mr-4" />
                                                                                Opp
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="dome-icon-btn"
                                                                                title="Update Contact Notes / Channel"
                                                                                onClick={(e) => handleOpenContactModal(lead, e)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faComments} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="dome-icon-btn dome-icon-btn-danger"
                                                                                title="Mark as Rejected"
                                                                                onClick={(e) => handleOpenRejectModal(lead, e)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faBan} />
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-secondary text-11">
                                                                            View Only
                                                                        </span>
                                                                    )
                                                                )}

                                                                {/* Status 3: Opportunity Created */}
                                                                {effStatus === 'Opportunity Created' && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn-dome-secondary btn-xs"
                                                                        onClick={() => navigate('/opportunities')}
                                                                        title={`View ${linkedOpps.length > 0 ? `${linkedOpps.length} ` : ''}converted deal(s) in Pipeline`}
                                                                    >
                                                                        <FontAwesomeIcon icon={faBriefcase} className="icon-mr-4" />
                                                                        Deal{linkedOpps.length > 1 ? ` (${linkedOpps.length})` : ''}
                                                                    </button>
                                                                )}

                                                                {/* Status 4: Rejected / Dead */}
                                                                {effStatus === 'Rejected' && (
                                                                    isUserAllowed ? (
                                                                        <button
                                                                            type="button"
                                                                            className="btn-dome-secondary btn-xs"
                                                                            title="Re-open lead and restart outreach"
                                                                            onClick={(e) => handleReopenLead(lead, 'New Lead', e)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faUndo} className="icon-mr-4" />
                                                                            Re-open
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-secondary text-11">
                                                                            View Only
                                                                        </span>
                                                                    )
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* DOME Table Footer Pagination */}
                            <div className="dome-table-footer">
                                <span className="dome-table-counter">
                                    Showing {paginatedLeads.length === 0 ? 0 : (currentPage - 1) * entries + 1}&ndash;{Math.min(currentPage * entries, filteredLeads.length)} of {filteredLeads.length} entries
                                </span>

                                <div className="dome-pagination-controls">
                                    <button
                                        type="button"
                                        className="dome-page-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(p => p - 1)}
                                    >
                                        Prev
                                    </button>

                                    <span className="dome-page-indicator">Page {currentPage} of {totalPages || 1}</span>

                                    <button
                                        type="button"
                                        className="dome-page-btn"
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        onClick={() => setCurrentPage(p => p + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ================= VIEW MODE 2: KANBAN PIPELINE BOARD (4 COLUMNS) ================= */}
                    {viewMode === 'kanban' && (
                        <div className="kanban-padding-wrap">
                            <div className="approaching-kanban-board kanban-grid-4col">

                                {/* COLUMN 1: NEW LEADS */}
                                <div className="kanban-column col-new">
                                    <div className="kanban-col-header">
                                        <div className="col-title-wrap">
                                            <span className="kanban-status-dot dot-blue"></span>
                                            <h3>New Leads</h3>
                                        </div>
                                        <span className="kanban-count-pill pill-blue">
                                            {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'New Lead').length}
                                        </span>
                                    </div>

                                    <div className="kanban-cards-scroll">
                                        {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'New Lead').length === 0 ? (
                                            <p className="kanban-empty-text">
                                                No new leads pending outreach
                                            </p>
                                        ) : (
                                            filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'New Lead').map(lead => (
                                                <div
                                                    key={lead.id}
                                                    className="kanban-lead-card"
                                                    onClick={() => setDetailAccount(lead)}
                                                >
                                                    <div className="k-card-header">
                                                        <span className={`k-priority-tag p-${(lead.priority || 'medium').toLowerCase()}`}>
                                                            {lead.priority || 'Medium'}
                                                        </span>
                                                        <div className="k-direct-links" onClick={(e) => e.stopPropagation()}>
                                                            {lead.phone && <a href={`tel:${lead.phone}`} title="Call"><FontAwesomeIcon icon={faPhone} /></a>}
                                                            {lead.email && <a href={`mailto:${lead.email}`} title="Email"><FontAwesomeIcon icon={faEnvelope} /></a>}
                                                            {lead.linkedin && <a href={formatLinkedInUrl(lead.linkedin)} target="_blank" rel="noopener noreferrer" title="LinkedIn"><LinkedInIcon /></a>}
                                                        </div>
                                                    </div>

                                                    <h4 className="k-account-name">{lead.name}</h4>
                                                    <p className="k-contact-sub">
                                                        {lead.contactPerson ? `${lead.contactPerson}${lead.designation ? ` (${lead.designation})` : ''}` : (lead.city || 'No contact specified')}
                                                    </p>

                                                    <div className="k-card-footer" onClick={(e) => e.stopPropagation()}>
                                                        <div className="k-rep-info">
                                                            <span className="k-rep-initials">
                                                                {(lead.responsibleName || lead.assignedToName || 'U').slice(0, 2).toUpperCase()}
                                                            </span>
                                                            <span className="k-rep-name">
                                                                {lead.responsibleName || lead.assignedToName || '—'}
                                                            </span>
                                                        </div>

                                                        {canManageLead(lead) ? (
                                                            <div className="kanban-action-pair">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-mark-contact k-action-btn"
                                                                    onClick={(e) => handleOpenContactModal(lead, e)}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} /> Log
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn-icon-more btn-kanban-reject"
                                                                    onClick={(e) => handleOpenRejectModal(lead, e)}
                                                                    title="Mark as Rejected"
                                                                >
                                                                    <FontAwesomeIcon icon={faBan} className="icon-fs-10" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="kanban-view-only-text">
                                                                View Only
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* COLUMN 2: CONTACTED LEADS */}
                                <div className="kanban-column col-contacted">
                                    <div className="kanban-col-header">
                                        <div className="col-title-wrap">
                                            <span className="kanban-status-dot dot-green"></span>
                                            <h3>Contacted</h3>
                                        </div>
                                        <span className="kanban-count-pill pill-green">
                                            {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Contacted').length}
                                        </span>
                                    </div>

                                    <div className="kanban-cards-scroll">
                                        {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Contacted').length === 0 ? (
                                            <p className="kanban-empty-text">
                                                No contacted leads yet
                                            </p>
                                        ) : (
                                            filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Contacted').map(lead => (
                                                <div
                                                    key={lead.id}
                                                    className="kanban-lead-card card-contacted"
                                                    onClick={() => setDetailAccount(lead)}
                                                >
                                                    <div className="k-card-header">
                                                        {lead.contactMethod ? (
                                                            <span className="k-contact-method-tag">
                                                                {lead.contactMethod}
                                                            </span>
                                                        ) : (
                                                            <span className={`k-priority-tag p-${(lead.priority || 'medium').toLowerCase()}`}>
                                                                {lead.priority || 'Medium'}
                                                            </span>
                                                        )}
                                                        <div className="k-direct-links" onClick={(e) => e.stopPropagation()}>
                                                            {lead.phone && <a href={`tel:${lead.phone}`}><FontAwesomeIcon icon={faPhone} /></a>}
                                                            {lead.email && <a href={`mailto:${lead.email}`}><FontAwesomeIcon icon={faEnvelope} /></a>}
                                                            {lead.linkedin && <a href={formatLinkedInUrl(lead.linkedin)} target="_blank" rel="noopener noreferrer"><LinkedInIcon /></a>}
                                                        </div>
                                                    </div>

                                                    <h4 className="k-account-name">{lead.name}</h4>
                                                    <p className="k-contact-sub">
                                                        {lead.contactPerson ? `${lead.contactPerson}` : (lead.city || 'Lead in contact')}
                                                    </p>


                                                    <div className="k-card-footer" onClick={(e) => e.stopPropagation()}>
                                                        <span className="k-contacted-at-txt">
                                                            {formatDate(lead.contactedAt)}
                                                        </span>

                                                        {canManageLead(lead) ? (
                                                            <div className="kanban-action-pair">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-create-opp-action k-action-btn"
                                                                    onClick={(e) => handleOpenCreateOppModal(lead, e)}
                                                                >
                                                                    <FontAwesomeIcon icon={faPlus} /> Opp
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn-icon-more btn-kanban-reject"
                                                                    onClick={(e) => handleOpenRejectModal(lead, e)}
                                                                    title="Mark as Rejected"
                                                                >
                                                                    <FontAwesomeIcon icon={faBan} className="icon-fs-10" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="kanban-view-only-text">
                                                                View Only
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                {/* COLUMN 3: OPPORTUNITY CREATED */}
                                <div className="kanban-column col-converted">
                                    <div className="kanban-col-header">
                                        <div className="col-title-wrap">
                                            <span className="kanban-status-dot dot-purple"></span>
                                            <h3>Opp Created</h3>
                                        </div>
                                        <span className="kanban-count-pill pill-purple">
                                            {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Opportunity Created').length}
                                        </span>
                                    </div>

                                    <div className="kanban-cards-scroll">
                                        {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Opportunity Created').length === 0 ? (
                                            <p className="kanban-empty-text">
                                                No converted opportunities yet
                                            </p>
                                        ) : (
                                            filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Opportunity Created').map(lead => {
                                                const linkedOpps = getLeadOpps(lead);
                                                const firstOpp = linkedOpps[0];

                                                return (
                                                    <div
                                                        key={lead.id}
                                                        className="kanban-lead-card card-converted"
                                                        onClick={() => setDetailAccount(lead)}
                                                    >
                                                        <div className="k-card-header">
                                                            {firstOpp ? (
                                                                <span className="k-opp-stage-tag">{firstOpp.Stage}</span>
                                                            ) : (
                                                                <span className="k-opp-stage-tag">Converted</span>
                                                            )}
                                                            <span className="k-opp-count-badge">
                                                                {linkedOpps.length > 0 ? `${linkedOpps.length} Deal${linkedOpps.length > 1 ? 's' : ''}` : '1 Deal'}
                                                            </span>
                                                        </div>

                                                        <h4 className="k-account-name">{lead.name}</h4>
                                                        <p className="k-contact-sub">
                                                            {lead.contactPerson || lead.city || 'Converted client'}
                                                        </p>

                                                        {firstOpp && (
                                                            <div className="k-opp-mini-preview">
                                                                <strong>{firstOpp.OpprName}</strong>
                                                                <span>${(Number(firstOpp.Amount) || 0).toLocaleString('en-US')}</span>
                                                            </div>
                                                        )}

                                                        <div className="k-card-footer" onClick={(e) => e.stopPropagation()}>
                                                            <div className="k-rep-info">
                                                                <span className="k-rep-initials">
                                                                    {(lead.responsibleName || lead.assignedToName || 'U').slice(0, 2).toUpperCase()}
                                                                </span>
                                                                <span className="k-rep-name">
                                                                    {lead.responsibleName || lead.assignedToName || '—'}
                                                                </span>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-view-opp-action k-action-btn"
                                                                onClick={() => navigate('/opportunities')}
                                                            >
                                                                View Deal <FontAwesomeIcon icon={faChevronRight} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* COLUMN 4: REJECTED / DEAD */}
                                <div className="kanban-column kanban-col-dead">
                                    <div className="kanban-col-header">
                                        <div className="col-title-wrap">
                                            <span className="kanban-status-dot dot-dead"></span>
                                            <h3>Rejected / Dead</h3>
                                        </div>
                                        <span className="kanban-count-pill pill-dead">
                                            {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Rejected').length}
                                        </span>
                                    </div>

                                    <div className="kanban-cards-scroll">
                                        {filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Rejected').length === 0 ? (
                                            <p className="kanban-empty-text">
                                                No rejected leads
                                            </p>
                                        ) : (
                                            filteredLeads.filter(l => getLeadEffectiveStatus(l) === 'Rejected').map(lead => (
                                                <div
                                                    key={lead.id}
                                                    className="kanban-lead-card kanban-card-dead"
                                                    onClick={() => setDetailAccount(lead)}
                                                >
                                                    <div className="k-card-header">
                                                        <span className="k-priority-tag priority-tag-dead">
                                                            Disqualified
                                                        </span>
                                                        <div className="k-direct-links" onClick={(e) => e.stopPropagation()}>
                                                            {lead.phone && <a href={`tel:${lead.phone}`}><FontAwesomeIcon icon={faPhone} /></a>}
                                                            {lead.email && <a href={`mailto:${lead.email}`}><FontAwesomeIcon icon={faEnvelope} /></a>}
                                                        </div>
                                                    </div>

                                                    <h4 className="k-account-name account-name-dead">{lead.name}</h4>
                                                    <p className="k-contact-sub">
                                                        {lead.contactPerson || lead.city || 'Dead lead'}
                                                    </p>

                                                    <div className="k-card-footer" onClick={(e) => e.stopPropagation()}>
                                                        <span className="text-dead-disqualified">
                                                            Disqualified
                                                        </span>

                                                        {canManageLead(lead) ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-secondary k-action-btn"
                                                                onClick={(e) => handleReopenLead(lead, 'New Lead', e)}
                                                            >
                                                                <FontAwesomeIcon icon={faUndo} /> Re-open
                                                            </button>
                                                        ) : (
                                                            <span className="kanban-view-only-text">
                                                                View Only
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                </div>

                {/* ================= MODAL 1: LOG OUTREACH / MARK CONTACTED ================= */}
                {contactModal.open && (
                    <div className="modal-overlay" onClick={() => setContactModal({ open: false, lead: null, method: 'Phone Call', notes: '' })}>
                        <div className="modal modal-contact-log" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <div className="modal-title-left">
                                    <FontAwesomeIcon icon={faCheckCircle} className="modal-icon-contact" />
                                    <h3>Log Outreach &amp; Mark as Contacted</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setContactModal({ open: false, lead: null, method: 'Phone Call', notes: '' })}
                                    className="modal-btn-close"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveContactLog}>
                                <div className="modal-body modal-body-stack">

                                    {/* Lead Preview Box */}
                                    <div className="lead-summary-box">
                                        <div className="preview-box-header">
                                            <strong className="preview-lead-name">{contactModal.lead?.name}</strong>
                                            <span className="preview-lead-id">{contactModal.lead?.id}</span>
                                        </div>
                                        <p className="preview-lead-contact">
                                            {contactModal.lead?.contactPerson ? `Contact: ${contactModal.lead?.contactPerson} (${contactModal.lead?.designation || 'Representative'})` : 'No primary contact recorded'}
                                        </p>
                                        <div className="preview-channels-row">
                                            {contactModal.lead?.phone && <span>📞 {contactModal.lead?.phone}</span>}
                                            {contactModal.lead?.email && <span>✉️ {contactModal.lead?.email}</span>}
                                        </div>
                                    </div>

                                    {/* Contact Method Selection */}
                                    <div className="input-group">
                                        <label>Outreach Channel / Method <span className="text-danger">*</span></label>
                                        <select
                                            value={contactModal.method}
                                            onChange={(e) => setContactModal(c => ({ ...c, method: e.target.value }))}
                                            required
                                        >
                                            {CONTACT_METHODS.map(m => (
                                                <option key={m.value} value={m.value}>{m.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Outreach Notes */}
                                    <div className="input-group">
                                        <label>Discussion Summary / Outreach Notes</label>
                                        <textarea
                                            rows="3"
                                            placeholder="e.g. Connected via phone call with Dr. Sarah. Sent clinical brochure and scheduled follow-up for next Tuesday."
                                            value={contactModal.notes}
                                            onChange={(e) => setContactModal(c => ({ ...c, notes: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => setContactModal({ open: false, lead: null, method: 'Phone Call', notes: '' })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-brand"
                                    >
                                        <FontAwesomeIcon icon={faCheck} className="icon-mr-6" />
                                        Save &amp; Mark as Contacted
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ================= MODAL 2: CREATE OPPORTUNITY FROM CONTACTED LEAD ================= */}
                {oppModal.open && (
                    <div className="modal-overlay" onClick={() => setOppModal({ open: false, lead: null, oppName: '', amount: 5000, stage: 'Qualification', closeDate: '', notes: '' })}>
                        <div className="modal modal-opp-create" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <div className="modal-title-left">
                                    <FontAwesomeIcon icon={faBriefcase} className="modal-icon-opp" />
                                    <h3>Create Opportunity from Lead</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setOppModal({ open: false, lead: null, oppName: '', amount: 5000, stage: 'Qualification', closeDate: '', notes: '' })}
                                    className="modal-btn-close"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveOpportunity}>
                                <div className="modal-body">
                                    <div className="form-grid-2col">

                                        {/* Opportunity Name */}
                                        <div className="input-group input-group-span-2">
                                            <label>Opportunity Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                value={oppModal.oppName}
                                                onChange={(e) => setOppModal(o => ({ ...o, oppName: e.target.value }))}
                                                placeholder="e.g. Acme Hospital - System Implementation"
                                                required
                                            />
                                        </div>

                                        {/* Linked Account */}
                                        <div className="input-group">
                                            <label>Account / Organization</label>
                                            <input
                                                type="text"
                                                value={oppModal.lead?.name || ''}
                                                disabled
                                                className="input-readonly-neutral"
                                            />
                                        </div>

                                        {/* Deal Value Amount ($) */}
                                        <div className="input-group">
                                            <label>Expected Deal Value ($) <span className="text-danger">*</span></label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="100"
                                                value={oppModal.amount}
                                                onChange={(e) => setOppModal(o => ({ ...o, amount: e.target.value }))}
                                                required
                                            />
                                        </div>

                                        {/* Sales Stage */}
                                        <div className="input-group">
                                            <label>Initial Sales Stage</label>
                                            <select
                                                value={oppModal.stage}
                                                onChange={(e) => setOppModal(o => ({ ...o, stage: e.target.value }))}
                                            >
                                                {OPPORTUNITY_STAGES.map(s => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Expected Close Date */}
                                        <div className="input-group">
                                            <label>Target Close Date</label>
                                            <input
                                                type="date"
                                                value={oppModal.closeDate}
                                                onChange={(e) => setOppModal(o => ({ ...o, closeDate: e.target.value }))}
                                            />
                                        </div>

                                        {/* Opportunity Notes */}
                                        <div className="input-group input-group-span-2">
                                            <label>Opportunity Requirements &amp; Engagement Notes</label>
                                            <textarea
                                                rows="3"
                                                value={oppModal.notes}
                                                onChange={(e) => setOppModal(o => ({ ...o, notes: e.target.value }))}
                                                placeholder="Describe project requirements, trial scope, or key decision makers..."
                                            />
                                        </div>

                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => setOppModal({ open: false, lead: null, oppName: '', amount: 5000, stage: 'Qualification', closeDate: '', notes: '' })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-brand"
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="icon-mr-6" />
                                        Create Opportunity
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ================= MODAL 3: MARK AS REJECTED / DEAD ================= */}
                {rejectModal.open && (
                    <div className="modal-overlay" onClick={() => setRejectModal({ open: false, lead: null, reason: 'Not Interested / Budget Constraints', notes: '' })}>
                        <div className="modal modal-lead-reject" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <div className="modal-title-left">
                                    <FontAwesomeIcon icon={faBan} className="modal-icon-reject" />
                                    <h3>Mark Lead as Rejected / Dead</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setRejectModal({ open: false, lead: null, reason: 'Not Interested / Budget Constraints', notes: '' })}
                                    className="modal-btn-close"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveRejection}>
                                <div className="modal-body modal-body-stack">

                                    <div className="lead-summary-box summary-box-reject">
                                        <strong className="reject-lead-name">{rejectModal.lead?.name}</strong>
                                        <p className="reject-lead-warning">
                                            Marking this lead as rejected will set its outreach status to Rejected and exclude it from approaching achievements.
                                        </p>
                                    </div>

                                    {/* Reason */}
                                    <div className="input-group">
                                        <label>Rejection / Disqualification Reason <span className="text-danger">*</span></label>
                                        <select
                                            value={rejectModal.reason}
                                            onChange={(e) => setRejectModal(r => ({ ...r, reason: e.target.value }))}
                                            required
                                        >
                                            {REJECTION_REASONS.map(r => (
                                                <option key={r} value={r}>{r}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Notes */}
                                    <div className="input-group">
                                        <label>Additional Notes / Feedback</label>
                                        <textarea
                                            rows="3"
                                            placeholder="Provide context on why lead was disqualified or declined..."
                                            value={rejectModal.notes}
                                            onChange={(e) => setRejectModal(r => ({ ...r, notes: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => setRejectModal({ open: false, lead: null, reason: 'Not Interested / Budget Constraints', notes: '' })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-danger"
                                    >
                                        <FontAwesomeIcon icon={faBan} className="icon-mr-6" />
                                        Confirm Rejection
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ================= MODAL 4: SalesVault LIGHTNING READ VIEW MODAL ================= */}
                <AccountDetailModal
                    isOpen={!!detailAccount}
                    account={detailAccount}
                    users={users}
                    onClose={() => setDetailAccount(null)}
                    onEdit={() => {
                        setDetailAccount(null);
                        navigate('/leads');
                    }}
                />

            </div>
        </Layout>
    );
}
